import React from 'react';

/**
 * Cabecera de tabla del sistema.
 *
 * Había una por sección y ninguna coincidía: Socios en versalitas grises sobre
 * banda gris, Almacén igual pero con otro gris, Cajas con tracking, Pagos
 * externos con semibold, Personal con `bg-gray-100`, Preinscripciones con `<th>`
 * pelados… y Entradas, que es la buena, en sentence case, 14 en negrita oscura y
 * SIN banda de fondo.
 *
 * Manda la de Entradas, y por una razón: las versalitas grises son de un tiempo
 * en que la cabecera era decoración: se leen peor, obligan a agrandar el ojo
 * para nada y compiten con el contenido en vez de encabezarlo. Un encabezado de
 * columna es una etiqueta que se lee, no un adorno.
 *
 * Cada tabla sigue eligiendo sus columnas y sus anchos; lo que se comparte es
 * la forma.
 */

export function TableHead({ children, className = '' }) {
  return (
    <thead className={className}>
      <tr className="border-b border-[var(--border)]">{children}</tr>
    </thead>
  );
}

const ALINEACION = {
  left: 'text-left',
  right: 'text-right',
  center: 'text-center',
};

/*
 * El padding por defecto CEDE ante el del caller. En Tailwind el orden del
 * atributo no decide nada — decide el orden de la hoja generada, y ahí `.py-4`
 * va DESPUÉS de `.py-1.5`/`.py-2.5`: un `<Td className="py-1.5">` renderizaba
 * py-4 y las tablas compactas (log de importación de Socios, filas de Pagos)
 * se inflaban en silencio. Por eso se detecta si el caller trae su propio
 * padding en cada eje y solo entonces se omite el del sistema.
 *
 * Vale para Th y para Td, y eso importa: mientras solo cedía Td, un
 * `<Th className="px-3 py-2">` sobre celdas de `px-3` se quedaba en px-4 y la
 * cabecera desalineaba con su propia columna, sin error ni aviso. Dos dueños
 * para la misma propiedad, otra vez.
 */
const trae = (re, cls) => re.test(cls);
const RE_PY = /(^|\s)(p|py|pt|pb)-/;
const RE_PX = /(^|\s)(p|px|pl|pr)-/;
// Misma historia con el alineado vertical: `.align-middle` va DESPUÉS de
// `.align-top` en la hoja de Tailwind, así que un `<Td className="align-top">`
// se centraba igual. Importa en las celdas de dos líneas —el detalle de
// Actividad, el usuario con su correo debajo—: centrarlas descoloca la fila
// respecto a las columnas de una sola línea.
const RE_ALIGN = /(^|\s)align-/;
const padding = (className, x, y) =>
  `${trae(RE_PX, className) ? '' : `${x} `}${trae(RE_PY, className) ? '' : `${y} `}`;

export function Th({ children, align = 'left', className = '', ...resto }) {
  return (
    <th
      scope="col"
      className={`${padding(className, 'px-4', 'py-3')}text-sm font-bold text-[var(--foreground)] ${ALINEACION[align] || ALINEACION.left} ${className}`}
      {...resto}
    >
      {children}
    </th>
  );
}

/**
 * Fila. `Club_Table_Row` (Figma 3296:18396): 60 de alto y una línea de
 * separación abajo — nada de bandas alternas ni de fondos por fila.
 */
export function Tr({ children, className = '', ...resto }) {
  return (
    <tr
      className={`border-b border-[var(--border)] transition-colors last:border-b-0 hover:bg-[var(--muted)] ${className}`}
      {...resto}
    >
      {children}
    </tr>
  );
}

/**
 * Celda. El frame le da 16 de padding por los cuatro lados. En el código había
 * `py-4` en Socios, `py-3` en Almacén y Tiendas, `px-3 py-3` en Cajas y
 * `px-4 py-3` en Entradas: cuatro alturas de fila para la misma tabla.
 *
 * NO fija el cuerpo del texto, a propósito. El frame dice `Text-xs/Regular` para
 * la celda corriente, pero buena parte del producto usa 14, y bajarlo a 12 de
 * golpe encogería texto en nueve pantallas sin que nadie lo haya pedido. Aquí se
 * unifica la geometría; el cuerpo se mira aparte y con capturas delante.
 */
export function Td({ children, align = 'left', className = '', ...resto }) {
  const vertical = trae(RE_ALIGN, className) ? '' : 'align-middle ';
  return (
    <td
      className={`${padding(className, 'px-4', 'py-4')}${vertical}${ALINEACION[align] || ALINEACION.left} ${className}`}
      {...resto}
    >
      {children}
    </td>
  );
}
